// Client-side image compression utility.
//
// The portfolio admin uploader uses this to resize/compress images before
// uploading them to the backend as base64 data URLs. The backend caps each
// image at 3MB; this helper aggressively keeps images well under that limit
// while preserving good visual quality for a portfolio gallery.

const DEFAULTS = {
  maxDimension: 1600, // longest edge
  quality: 0.82,
  mimeType: "image/jpeg",
};

/**
 * Read a File into an HTMLImageElement.
 */
function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });
}

/**
 * Convert a base64 data URL into an approximate byte count.
 */
export function dataUrlByteLength(dataUrl) {
  if (!dataUrl) return 0;
  const commaIdx = dataUrl.indexOf(",");
  if (commaIdx === -1) return 0;
  const b64 = dataUrl.slice(commaIdx + 1);
  // base64: 4 chars = 3 bytes, minus padding
  const padding = (b64.endsWith("==") ? 2 : b64.endsWith("=") ? 1 : 0);
  return Math.floor((b64.length * 3) / 4) - padding;
}

/**
 * Compress an image File to a JPEG data URL under the given byte cap.
 * Falls back to lower quality settings if the initial pass is still too big.
 */
export async function compressImageFile(file, opts = {}) {
  const settings = { ...DEFAULTS, ...opts };
  const maxBytes = opts.maxBytes ?? 2.6 * 1024 * 1024; // 2.6MB safety margin under 3MB cap

  const img = await loadImage(file);
  const longest = Math.max(img.width, img.height);
  const scale = longest > settings.maxDimension ? settings.maxDimension / longest : 1;
  const targetW = Math.round(img.width * scale);
  const targetH = Math.round(img.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, targetW, targetH);

  // Successive quality reductions if needed.
  let quality = settings.quality;
  let dataUrl = canvas.toDataURL(settings.mimeType, quality);
  let bytes = dataUrlByteLength(dataUrl);
  while (bytes > maxBytes && quality > 0.35) {
    quality -= 0.1;
    dataUrl = canvas.toDataURL(settings.mimeType, quality);
    bytes = dataUrlByteLength(dataUrl);
  }

  return {
    data_url: dataUrl,
    size: bytes,
    width: targetW,
    height: targetH,
    content_type: settings.mimeType,
    filename: file.name || "image.jpg",
  };
}

export function isImageFile(file) {
  return !!file && typeof file.type === "string" && file.type.startsWith("image/");
}

export function formatBytes(bytes) {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}
